import type { ReactNode } from 'react';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { easings } from '../lib/motion';

const TAGS = {
  div: motion.div,
  section: motion.section,
  article: motion.article,
  ul: motion.ul,
  ol: motion.ol,
  li: motion.li,
  span: motion.span,
} as const;
type Tag = keyof typeof TAGS;

const ENTER = { duration: 0.32, ease: easings.snappy };

export interface RevealProps {
  children?: ReactNode;
  as?: Tag;
  className?: string;
  /** Atraso em segundos. */
  delay?: number;
  /** Deslocamento vertical inicial em px (padrão 8). */
  y?: number;
  /** Anima quando entra na tela (rolagem) em vez de ao montar. */
  inView?: boolean;
  id?: string;
}

/** Entrada suave (sobe e aparece). Com "reduzir movimento", só aparece. */
export function Reveal({ children, as = 'div', className, delay = 0, y = 8, inView = false, id }: RevealProps) {
  const reduce = useReducedMotion();
  const Comp = TAGS[as];
  const hidden = { opacity: 0, y: reduce ? 0 : y };
  const shown = { opacity: 1, y: 0 };
  return (
    <Comp
      id={id}
      className={className}
      initial={hidden}
      {...(inView ? { whileInView: shown, viewport: { once: true, margin: '0px 0px -10% 0px' } } : { animate: shown })}
      transition={{ ...ENTER, delay }}
    >
      {children}
    </Comp>
  );
}

const containerVariants = (stagger: number, delay: number): Variants => ({
  hidden: {},
  shown: { transition: { staggerChildren: stagger, delayChildren: delay } },
});

export interface StaggerProps {
  children?: ReactNode;
  as?: Tag;
  className?: string;
  /** Intervalo entre itens em segundos (padrão 0,045). */
  stagger?: number;
  delay?: number;
  inView?: boolean;
}

/** Contêiner que faz os <StaggerItem> entrarem em cascata leve. */
export function Stagger({ children, as = 'div', className, stagger = 0.045, delay = 0, inView = false }: StaggerProps) {
  const Comp = TAGS[as];
  return (
    <Comp
      className={className}
      variants={containerVariants(stagger, delay)}
      initial="hidden"
      {...(inView ? { whileInView: 'shown', viewport: { once: true, margin: '0px 0px -10% 0px' } } : { animate: 'shown' })}
    >
      {children}
    </Comp>
  );
}

export function StaggerItem({ children, as = 'div', className, y = 8 }: { children?: ReactNode; as?: Tag; className?: string; y?: number }) {
  const reduce = useReducedMotion();
  const Comp = TAGS[as];
  const variants: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : y },
    shown: { opacity: 1, y: 0, transition: ENTER },
  };
  return (
    <Comp className={className} variants={variants}>
      {children}
    </Comp>
  );
}
